import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Instagram, ShoppingBag, Star, UtensilsCrossed } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import { MENU, SHOP } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

function MenuItemRow({ item, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.3), ease: EASE }}
            className="flex items-baseline py-3"
            data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="min-w-0">
                <p className="flex items-center gap-2 font-semibold text-ink">
                    {item.name}
                    {item.best && (
                        <Star className="h-3.5 w-3.5 fill-accent text-accent" aria-label="favorit" />
                    )}
                </p>
                {item.note && (
                    <p className="text-sm text-ink-muted">{item.note}</p>
                )}
            </div>
            <span className="leader" aria-hidden="true" />
            <span className="shrink-0 font-display text-lg font-extrabold text-accent">
                {item.price}
            </span>
        </motion.div>
    );
}

export default function MenuPage() {
    const [active, setActive] = useState(MENU[0].id);
    const category = MENU.find((c) => c.id === active);

    return (
        <div className="pt-24 lg:pt-32" data-testid="menu-page">
            <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <span className="eyebrow">Yuhuku Kopi</span>
                </Reveal>
                <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
                    <Reveal delay={0.08}>
                        <h1 className="font-display text-5xl font-extrabold tracking-tighter text-ink sm:text-6xl lg:text-7xl">
                            The <span className="text-accent">Menu.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="max-w-xs pb-2 text-sm leading-relaxed text-ink-muted">
                            Harga dalam ribuan rupiah. Semua bisa dipesan di
                            cafe atau lewat GoFood.
                        </p>
                    </Reveal>
                </div>
            </header>

            <div className="sticky top-[68px] z-30 mt-10 border-y border-ink/5 bg-bg/90 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-3.5 sm:px-6 lg:px-8">
                    {MENU.map((cat) => {
                        const isActive = cat.id === active;
                        return (
                            <button
                                key={cat.id}
                                data-testid={`menu-tab-${cat.id}`}
                                onClick={() => setActive(cat.id)}
                                className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                                    isActive
                                        ? "bg-accent text-white shadow-[0_8px_20px_rgb(200_50_50/0.25)]"
                                        : "border border-ink/10 bg-white text-ink hover:border-ink/25"
                                }`}
                            >
                                {cat.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.35, ease: EASE }}
                    >
                        <div className="flex flex-wrap items-center gap-4">
                            {category.image ? (
                                <img
                                    src={category.image}
                                    alt={category.label}
                                    className="h-14 w-14 rounded-2xl object-cover"
                                />
                            ) : (
                                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-soft text-accent">
                                    <UtensilsCrossed className="h-6 w-6" />
                                </span>
                            )}
                            <div>
                                <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink sm:text-4xl">
                                    {category.label}
                                </h2>
                                <p className="text-sm text-ink-muted">
                                    {category.note} ·{" "}
                                    {category.groups.reduce(
                                        (n, g) => n + g.items.length,
                                        0
                                    )}{" "}
                                    menu
                                </p>
                            </div>
                        </div>

                        <div
                            className={`mt-10 grid gap-6 ${
                                category.groups.length > 1 ? "lg:grid-cols-2" : "max-w-3xl"
                            }`}
                        >
                            {category.groups.map((group) => (
                                <div
                                    key={group.title}
                                    className="rounded-3xl border border-ink/5 bg-white p-6 shadow-soft sm:p-8"
                                >
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink-muted">
                                        {group.title}
                                    </p>
                                    <div className="mt-3 divide-y divide-ink/5">
                                        {group.items.map((item, i) => (
                                            <MenuItemRow
                                                key={item.name}
                                                item={item}
                                                index={i}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {category.extras && (
                            <div
                                className="mt-6 flex max-w-3xl flex-wrap items-center gap-x-8 gap-y-2 rounded-3xl border border-dashed border-ink/15 px-6 py-5"
                                data-testid="menu-extras"
                            >
                                <p className="text-sm font-bold uppercase tracking-[0.15em] text-matcha">
                                    Tambahan
                                </p>
                                {category.extras.map((e) => (
                                    <p
                                        key={e.name}
                                        className="flex items-baseline text-sm font-semibold text-ink"
                                    >
                                        {e.name}
                                        <span className="leader" />
                                        <span className="font-display text-base font-extrabold text-accent">
                                            {e.price}
                                        </span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <section className="px-4 pb-24 sm:px-6 lg:px-8">
                <Reveal className="mx-auto max-w-7xl">
                    <div className="overflow-hidden rounded-[2.5rem] bg-ink px-6 py-14 text-bg sm:px-12 lg:px-16">
                        <div className="max-w-2xl">
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                            Belum bisa milih?
                            </span>
                            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                                Try the Signature.
                            </h2>
                            <p className="mt-4 leading-relaxed text-bg/70">
                                Espresso 1 shot, creamer, brown sugar, dan susu
                                — menu andalan Yuhuku yang bikin nagih. Pesan
                                lewat GoFood atau DM Instagram.
                            </p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3.5">
                            <a
                                href={SHOP.gofood}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="menu-cta-gofood"
                                onClick={() =>
                                    trackEvent("cta_click", { cta: "gofood_menu" })
                                }
                                className="btn-primary"
                            >
                                <ShoppingBag className="h-4 w-4" /> Pesan di GoFood
                            </a>
                            <a
                                href={SHOP.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="menu-cta-instagram"
                                className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-bg transition-all hover:-translate-y-0.5 hover:border-white/50"
                            >
                                <Instagram className="inline h-4 w-4" /> DM Kami
                            </a>
                        </div>
                        <p className="mt-8 text-xs text-bg/40">
                            Ketersediaan menu dapat berubah. Info paling akurat
                            ada di Instagram {SHOP.instagramHandle}.
                        </p>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}